import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';

const CompanyContext = createContext(null);

export function CompanyProvider({ children }) {
  const { user } = useAuth();
  const [companies, setCompanies] = useState([]);
  const [activeCompany, setActiveCompanyState] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchCompanies = useCallback(async () => {
    try {
      const { data } = await api.get('/api/companies');
      setCompanies(data);
      const active = data.find(c => c.is_active) || data.find(c => c.is_default) || data[0] || null;
      setActiveCompanyState(active);
    } catch {
      setCompanies([]);
      setActiveCompanyState(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (user) {
      fetchCompanies();
    } else {
      setCompanies([]);
      setActiveCompanyState(null);
      setLoading(false);
    }
  }, [user, fetchCompanies]);

  const setActiveCompany = useCallback(async (company) => {
    try {
      await api.post(`/api/companies/${company._id}/set-active`);
      setActiveCompanyState(company);
      setCompanies(prev => prev.map(c => ({ ...c, is_active: c._id === company._id })));
    } catch (e) {
      console.error('Failed to set active company', e);
    }
  }, []);

  const refreshCompanies = useCallback(async () => {
    await fetchCompanies();
  }, [fetchCompanies]);

  return (
    <CompanyContext.Provider value={{ companies, activeCompany, setActiveCompany, refreshCompanies, loading }}>
      {children}
    </CompanyContext.Provider>
  );
}

export function useCompany() {
  return useContext(CompanyContext);
}
